import React, { useCallback, useEffect, useState } from 'react';

import { fetchIdentity, fetchStudies, fetchStudyDetail } from './api';
import { AuthError, ServiceUnavailableError, getDevSubject, setDevSubject } from './auth';
import { authMode, handleCallback, lastSignOutReason, signIn, signOut } from './cognito';
import { MyConfirmationRate } from './components/MyConfirmationRate';
import { Navbar } from './components/Navbar';
import { ReviewWorkspaceView } from './components/ReviewWorkspace';
import { UploadModal } from './components/UploadModal';
import { UserManagement } from './components/UserManagement';
import { PlatformOps } from './components/PlatformOps';
import { Experiments } from './components/Experiments';
import { CorpusReview } from './components/CorpusReview';
import { AuditWindows } from './components/AuditWindows';
import { TruthLabeller } from './components/TruthLabeller';
import { Worklist } from './components/Worklist';
import { Identity, StudyDetail, StudySummary } from './types';

/** Caps the silent auto-retry on a callback failure at one attempt — see the effect below. */
const CALLBACK_RETRY_KEY = 'scribe.callbackRetry';

/**
 * Fallback only. The real value is the acting tenant's `idle_logout_seconds`, served by
 * `/whoami` (§164.312(a)(2)(iii)) — per-contract, because a hospital whose clinicians share a
 * workstation in a corridor and a clinic where each reviewer has a laptop have genuinely
 * different exposure. Used only if an older server omits the field.
 */
const IDLE_LOGOUT_FALLBACK_MS = 43_200_000;

/**
 * How long the "you are about to be signed out" dialog counts down.
 *
 * It exists because the previous behaviour was to simply vanish: a reviewer mid-study saw a login
 * screen with no explanation, which is why this was reported as the application signing people
 * out at random. A warning also makes the timeout *work* — it gives back the one thing a silent
 * timeout takes away, which is the chance to say "I am still here".
 */
const IDLE_WARNING_MS = 60_000;

/**
 * The app no longer holds a user it made up.
 *
 * It previously initialised `currentUser` to a hardcoded technician and passed that identity
 * into every API call, which the server then trusted. Identity, tenancy and roles now come
 * from `GET /whoami` — the client's only job is to ask, display, and respect what comes back.
 */
export const App: React.FC = () => {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [authProblem, setAuthProblem] = useState<string | null>(null);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  // Distinguishes "not authenticated, try a silent redirect" from "authenticated but something
  // else went wrong" — only the former should auto-redirect, or a genuine outage would bounce
  // the tab through Cognito in a loop.
  const [wasUnauthenticated, setWasUnauthenticated] = useState(false);
  const [serviceDown, setServiceDown] = useState(false);

  const [studies, setStudies] = useState<StudySummary[]>([]);
  const [studyTotal, setStudyTotal] = useState(0);
  /** Epoch ms at which the idle timeout will fire, while the warning dialog is showing. */
  const [idleWarningAt, setIdleWarningAt] = useState<number | null>(null);
  const [selectedStudyId, setSelectedStudyId] = useState<string | null>(null);
  const [selectedStudy, setSelectedStudy] = useState<StudyDetail | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isPlatformOpen, setIsPlatformOpen] = useState(false);
  const [isExperimentsOpen, setIsExperimentsOpen] = useState(false);
  const [isCorpusOpen, setIsCorpusOpen] = useState(false);
  const [isMyStatsOpen, setIsMyStatsOpen] = useState(false);
  const [auditStudyId, setAuditStudyId] = useState<string | null>(null);
  const [truthStudyId, setTruthStudyId] = useState<string | null>(null);
  const [worklistError, setWorklistError] = useState<string | null>(null);

  const resolveIdentity = useCallback(async () => {
    setIsAuthResolved(false);
    try {
      setIdentity(await fetchIdentity());
      setServiceDown(false);
      setAuthProblem(null);
      setWasUnauthenticated(false);
    } catch (err) {
      setIdentity(null);
      setWasUnauthenticated(err instanceof AuthError && err.status === 401);
      setServiceDown(err instanceof ServiceUnavailableError);
      setAuthProblem(
        err instanceof AuthError
          ? err.message
          : err instanceof ServiceUnavailableError
            ? 'The Scribe service is not running right now. In dev it scales down outside working hours and takes a minute or two to start again.'
            : 'Could not reach the server to establish identity.',
      );
    } finally {
      setIsAuthResolved(true);
    }
  }, []);

  useEffect(() => {
    // Complete a Cognito redirect first, if this load is one, then establish identity.
    (async () => {
      if (authMode() === 'cognito') {
        try {
          await handleCallback();
          sessionStorage.removeItem(CALLBACK_RETRY_KEY);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Sign-in failed.';
          setAuthProblem(message);
          // A state/verifier mismatch or a token-exchange hiccup is transient — it's what the
          // now-fixed concurrent-signIn() race actually looked like (cognito.ts) — so retry once,
          // silently, via SilentSignIn rather than stranding the user on a manual "Sign in"
          // button. Capped at one retry, not unconditional: a genuinely persistent failure (bad
          // client id, wrong domain — the kind a broken build config produces) must still surface
          // instead of silently redirecting to Cognito and back forever, which is what an
          // uncapped retry did here once already (2026-09-14, before handleCallback() itself was
          // made idempotent against StrictMode's double-invoke — see cognito.ts).
          const alreadyRetried = sessionStorage.getItem(CALLBACK_RETRY_KEY) === '1';
          const isConfigError = message === 'Cognito is not configured for this build.';
          if (isConfigError || alreadyRetried) {
            sessionStorage.removeItem(CALLBACK_RETRY_KEY);
            setWasUnauthenticated(false);
          } else {
            sessionStorage.setItem(CALLBACK_RETRY_KEY, '1');
            setWasUnauthenticated(true);
          }
          setIsAuthResolved(true);
          return;
        }
      }
      await resolveIdentity();
    })();
  }, [resolveIdentity]);

  // Automatic logoff after inactivity (§164.312(a)(2)(iii)) — backend/app/config.py has carried
  // IDLE_LOGOUT_SECONDS since early on, but nothing anywhere ever actually enforced it; found
  // while chasing "signs out after an hour or two," which was really the *opposite* problem (no
  // real idle logout, but no working token refresh either — see cognito.ts's authorizedFetch).
  // Client-side only for now: the server has no notion of "idle" since every request is
  // independently authenticated, so this bounds exposure on a shared clinical workstation without
  // needing a server-side session to expire.
  useEffect(() => {
    if (!identity || authMode() !== 'cognito') return;
    const limitMs = (identity.idle_logout_seconds ?? 0) > 0
      ? (identity.idle_logout_seconds as number) * 1000
      : IDLE_LOGOUT_FALLBACK_MS;

    let lastActivity = Date.now();
    const bump = () => {
      lastActivity = Date.now();
      setIdleWarningAt(null);
    };
    const events: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    // A backgrounded tab emits none of the events above, so a reviewer working in another system
    // on the same machine used to be counted as absent. Coming back to the tab is itself a signal
    // that they are still at the workstation.
    const onVisible = () => { if (!document.hidden) bump(); };
    document.addEventListener('visibilitychange', onVisible);

    const timer = setInterval(() => {
      const idleFor = Date.now() - lastActivity;
      if (idleFor >= limitMs) {
        signOut(`You were signed out after ${Math.round(limitMs / 60_000)} minutes of inactivity.`);
      } else if (idleFor >= limitMs - IDLE_WARNING_MS) {
        setIdleWarningAt(lastActivity + limitMs);
      }
    }, 5_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
      setIdleWarningAt(null);
    };
  }, [identity]);

  const loadStudies = useCallback(async () => {
    // The worklist is PHI. A caller without a clinical role gets 403 by design — tenant admin
    // does not imply PHI access — so surface that rather than showing an empty queue, which
    // would look like "no studies" instead of "not permitted".
    if (!identity?.can_read_phi) {
      setStudies([]);
      return;
    }
    try {
      // One page, not the whole table (§13.6 R8). The worklist is ordered by how soon each study
      // is due, so the first page is the work that matters; `total` is carried so the header can
      // say how much there is rather than how much arrived.
      const page = await fetchStudies();
      setStudies(page.items);
      setStudyTotal(page.total);
      setWorklistError(null);
    } catch (err) {
      setWorklistError(err instanceof Error ? err.message : 'Failed to load the worklist.');
    }
  }, [identity]);

  useEffect(() => {
    if (!identity) return;
    void loadStudies();
    const timer = setInterval(() => void loadStudies(), 10000);
    return () => clearInterval(timer);
  }, [identity, loadStudies]);

  const handleSelectStudy = async (id: string) => {
    try {
      const detail = await fetchStudyDetail(id);
      setSelectedStudyId(id);
      setSelectedStudy(detail);
    } catch (err) {
      setWorklistError(err instanceof Error ? err.message : 'Failed to open the study.');
    }
  };

  const handleSwitchDevUser = (subject: string) => {
    setDevSubject(subject);
    setSelectedStudyId(null);
    setSelectedStudy(null);
    void resolveIdentity();
  };

  const staySignedIn = useCallback(() => {
    // The listeners in the idle effect above treat any real interaction as activity; this click
    // is one, so clearing the warning here is enough and there is no separate "reset" to call.
    setIdleWarningAt(null);
  }, []);

  if (!isAuthResolved) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center">
        Establishing identity…
      </div>
    );
  }

  if (!identity && authMode() === 'cognito' && wasUnauthenticated) {
    return <SilentSignIn />;
  }

  if (!identity) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="glass-panel max-w-lg space-y-4 rounded-2xl p-8">
          <h1 className="text-lg font-bold">{serviceDown ? 'Service unavailable' : 'Not signed in'}</h1>
          <p className="text-sm text-slate-400">{authProblem}</p>
          {serviceDown ? (
            <button
              onClick={() => void resolveIdentity()}
              className="rounded-lg border border-cyan-800 bg-cyan-950 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-900"
            >
              Retry
            </button>
          ) : authMode() === 'cognito' ? (
            <button
              onClick={() => void signIn()}
              className="rounded-lg border border-cyan-800 bg-cyan-950 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-900"
            >
              Sign in
            </button>
          ) : (
            <>
              <p className="text-xs text-slate-500">
                In local development the backend accepts a seeded subject via an{' '}
                <code className="text-slate-300">X-Dev-User</code> header. Pick one to continue —
                the server only honours this when it is running with both{' '}
                <code className="text-slate-300">AUTH_MODE=dev</code> and{' '}
                <code className="text-slate-300">APP_ENV=local</code>.
              </p>
              <div className="flex flex-wrap gap-2">
                {['dev-phys', 'dev-tech'].map((subject) => (
                  <button
                    key={subject}
                    onClick={() => handleSwitchDevUser(subject)}
                    className="rounded-lg border border-cyan-800 bg-cyan-950 px-3 py-1.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-900"
                  >
                    {subject}
                  </button>
                ))}
              </div>
              {getDevSubject() && (
                <p className="text-xs text-amber-400">
                  Current subject <code>{getDevSubject()}</code> was rejected — it may not be
                  provisioned in this database. Run <code>python -m scripts_seed</code>.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 font-sans text-slate-100">
      {idleWarningAt !== null && (
        <IdleWarning deadline={idleWarningAt} onStay={staySignedIn} />
      )}
      <Navbar
        identity={identity}
        onSwitchDevUser={handleSwitchDevUser}
        onOpenUpload={() => setIsUploadOpen(true)}
        onOpenPlatform={() => {
          setSelectedStudyId(null);
          setSelectedStudy(null);
          setIsUsersOpen(false);
          setIsCorpusOpen(false);
          setIsExperimentsOpen(false);
          setIsPlatformOpen(true);
        }}
        onOpenExperiments={() => {
          setSelectedStudyId(null);
          setSelectedStudy(null);
          setIsUsersOpen(false);
          setIsCorpusOpen(false);
          setIsPlatformOpen(false);
          setIsExperimentsOpen(true);
        }}
        onOpenUsers={() => {
          setIsExperimentsOpen(false);
          setSelectedStudyId(null);
          setSelectedStudy(null);
          setIsCorpusOpen(false);
          setIsUsersOpen(true);
        }}
        onOpenCorpus={() => {
          setIsExperimentsOpen(false);
          setSelectedStudyId(null);
          setSelectedStudy(null);
          setIsUsersOpen(false);
          setIsPlatformOpen(false);
          setAuditStudyId(null);
          setIsCorpusOpen(true);
        }}
        onOpenMyStats={() => {
          setIsExperimentsOpen(false);
          setSelectedStudyId(null);
          setSelectedStudy(null);
          setIsUsersOpen(false);
          setIsPlatformOpen(false);
          setIsCorpusOpen(false);
          setAuditStudyId(null);
          setIsMyStatsOpen(true);
        }}
        activeStudyId={selectedStudyId}
      />

      <main className="flex-1">
        {isExperimentsOpen ? (
          <Experiments onBack={() => setIsExperimentsOpen(false)} />
        ) : isPlatformOpen ? (
          <PlatformOps onBack={() => setIsPlatformOpen(false)} />
        ) : isMyStatsOpen ? (
          <MyConfirmationRate onBack={() => setIsMyStatsOpen(false)} />
        ) : truthStudyId ? (
          <TruthLabeller studyId={truthStudyId} onBack={() => setTruthStudyId(null)} />
        ) : auditStudyId ? (
          <AuditWindows studyId={auditStudyId} onBack={() => setAuditStudyId(null)} />
        ) : isCorpusOpen ? (
          <CorpusReview
            onBack={() => setIsCorpusOpen(false)}
            onOpenAudit={(id) => {
              setIsCorpusOpen(false);
              setAuditStudyId(id);
            }}
            onOpenTruth={(id) => {
              setIsCorpusOpen(false);
              setTruthStudyId(id);
            }}
          />
        ) : isUsersOpen ? (
          <UserManagement onBack={() => setIsUsersOpen(false)} />
        ) : !identity.can_read_phi ? (
          <div className="mx-auto max-w-2xl px-6 py-16">
            <div className="glass-panel space-y-3 rounded-2xl p-8">
              <h2 className="font-bold text-white">No clinical role</h2>
              <p className="text-sm text-slate-400">
                This account holds {identity.roles.join(', ') || 'no roles'}, which does not
                include PHI access. Administering users and reading patient waveforms are
                separate capabilities by design.
                {identity.roles.includes('TENANT_ADMIN') && (
                  <> Use <strong>Users</strong> above to create a clinician account, or add a
                  clinical role to this one.</>
                )}
              </p>
            </div>
          </div>
        ) : selectedStudyId && selectedStudy && (selectedStudy.status === 'INGESTING' || selectedStudy.status === 'INGEST_FAILED') ? (
          // No signal exists yet (or ever will, if ingest failed) — §13.6 I1/I2. Rendering the
          // full workspace here would fetch waveform and findings for a study that has neither,
          // which is exactly the "empty must say why, not look like nothing found" mistake §3.3
          // exists to prevent, so this is a distinct panel rather than a guard inside that screen.
          <div className="mx-auto max-w-2xl px-6 py-16">
            <div className="glass-panel space-y-4 rounded-2xl p-8">
              <button
                onClick={() => { setSelectedStudyId(null); setSelectedStudy(null); void loadStudies(); }}
                className="text-xs text-slate-400 hover:text-white"
              >
                ← Back to worklist
              </button>
              {selectedStudy.status === 'INGESTING' ? (
                <>
                  <h2 className="font-bold text-white">Ingesting {selectedStudy.reference}</h2>
                  <p className="text-sm text-slate-400">
                    Parsing the uploaded recording and building the review pyramid. This usually
                    takes well under a minute; analysis starts automatically once it finishes.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="font-bold text-rose-300">Ingest failed — {selectedStudy.reference}</h2>
                  <p className="text-sm text-slate-400">
                    {selectedStudy.ingest_error || 'The uploaded file could not be processed.'}
                  </p>
                  <p className="text-sm text-slate-500">Upload the recording again to retry.</p>
                </>
              )}
            </div>
          </div>
        ) : selectedStudyId && selectedStudy ? (
          <ReviewWorkspaceView
            studyId={selectedStudyId}
            study={selectedStudy}
            identity={identity}
            onBack={() => {
              setSelectedStudyId(null);
              setSelectedStudy(null);
              void loadStudies();
            }}
          />
        ) : (
          <Worklist
            studies={studies}
            total={studyTotal}
            error={worklistError}
            identity={identity}
            onSelectStudy={handleSelectStudy}
            onRefresh={() => void loadStudies()}
          />
        )}
      </main>

      <UploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUploaded={(id) => {
          setIsUploadOpen(false);
          setIsUsersOpen(false);
          void loadStudies();
          void handleSelectStudy(id);
        }}
      />
    </div>
  );
};

/** Side-effect kept out of App's render body: a redirect is a one-shot navigation, not a value to render. */
const SilentSignIn: React.FC = () => {
  // Read once, on mount: `lastSignOutReason` clears as it reads, so calling it during render
  // would lose the message on the next re-render.
  const [reason] = useState(() => lastSignOutReason());

  useEffect(() => {
    // **Not** when we signed the reviewer out deliberately. An inactivity sign-out ends Cognito's
    // hosted session too, so redirecting straight back would land on Cognito's own login form —
    // where nothing of ours can explain what happened, which is exactly the "it logs me out at
    // random" experience this is meant to end. Wait for a click instead.
    if (!reason) void signIn();
  }, [reason]);

  if (reason) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="glass-panel max-w-md space-y-4 rounded-2xl p-8 text-center">
          <h1 className="text-lg font-bold">Signed out</h1>
          <p className="text-sm text-slate-400">{reason}</p>
          <button
            onClick={() => void signIn()}
            className="rounded-lg border border-cyan-800 bg-cyan-950 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-900"
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center">
      Signing in…
    </div>
  );
};

/**
 * Counts down the last minute before an inactivity sign-out, and offers the obvious way out.
 *
 * The behaviour this replaces was to vanish without warning, which is why it was reported as the
 * application signing people out at random. Any mouse or key event dismisses it — the dialog is
 * what makes the timeout legible, the button is only there for a reviewer whose hands are off the
 * keyboard.
 */
const IdleWarning: React.FC<{ deadline: number; onStay: () => void }> = ({ deadline, onStay }) => {
  const [remaining, setRemaining] = useState(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const tick = setInterval(() => setRemaining(Math.max(0, deadline - Date.now())), 500);
    return () => clearInterval(tick);
  }, [deadline]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-6">
      <div className="glass-panel max-w-md space-y-3 rounded-2xl border border-amber-800 p-6">
        <h2 className="text-base font-bold text-amber-200">Still there?</h2>
        <p className="text-sm text-slate-300">
          You will be signed out in <strong className="mono">{Math.ceil(remaining / 1000)}s</strong> because
          this session has been inactive. Any work you have already saved is safe.
        </p>
        <button
          onClick={onStay}
          className="rounded-lg border border-cyan-800 bg-cyan-950 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-900"
        >
          Stay signed in
        </button>
      </div>
    </div>
  );
};

export default App;
